import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { TrendingUp, Users, Bot, MessageSquare, Heart, Loader2 } from "lucide-react";

const RANGES = [
  { id: 7, label: "7 dní" },
  { id: 14, label: "14 dní" },
  { id: 30, label: "30 dní" },
  { id: 90, label: "90 dní" },
];

const COLORS = {
  registrations: "#D4AF37",   // gold
  donations: "#22c55e",       // green
  ai: "#60a5fa",              // blue
  chat: "#f97316",            // orange
  visitors: "#a78bfa",        // soft purple (used sparingly)
};

const fmtDay = (iso) => {
  // "2026-05-23" → "23.5."
  const [, m, d] = iso.split("-");
  return `${parseInt(d, 10)}.${parseInt(m, 10)}.`;
};

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-black/95 border border-gold-700/50 p-3 shadow-xl backdrop-blur" data-testid="chart-tooltip">
      <div className="text-[10px] font-mono uppercase tracking-widest text-gold-500 mb-2">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center justify-between gap-4 text-xs">
          <span className="inline-flex items-center gap-2">
            <span className="w-2 h-2" style={{ background: p.color }} />
            <span className="text-zinc-300">{p.name}</span>
          </span>
          <span className="font-display font-bold text-white tabular-nums">{p.value}</span>
        </div>
      ))}
    </div>
  );
}

export default function AnalyticsChart() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = (d) => {
    setBusy(true);
    setError(null);
    api.get(`/admin/analytics/daily?days=${d}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || e.message || "Nepodarilo sa načítať"))
      .finally(() => setBusy(false));
  };

  useEffect(() => {
    load(days);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  if (error) {
    return (
      <div className="border border-red-700/40 bg-red-950/15 p-5" data-testid="analytics-error">
        <div className="font-display font-bold text-base text-red-300 mb-1">Graf návštevnosti — chyba načítania</div>
        <div className="text-xs text-zinc-400 mb-3">{error}</div>
        <div className="text-[10px] text-zinc-500 mb-3 font-mono">
          Tip: ak je to v produkcii — backend asi nie je deployovaný s novým endpointom `/api/admin/analytics/daily`. Skús „Re-deploy changes" v Emergent.
        </div>
        <button onClick={() => load(days)} className="wb-btn-ghost px-3 py-1.5 text-xs" data-testid="analytics-retry">
          Skúsiť znova
        </button>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="border border-zinc-800 bg-[var(--wb-surface)] p-5 h-72 flex items-center justify-center text-zinc-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Načítavam graf návštevnosti...
      </div>
    );
  }

  const series = data.series.map((d) => ({ ...d, dateShort: fmtDay(d.date) }));

  return (
    <div className="border border-zinc-800 bg-[var(--wb-surface)] p-5" data-testid="analytics-chart">
      {/* Header + range picker */}
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div>
          <h3 className="font-display font-bold text-lg inline-flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-gold-500" />
            Návštevnosť & aktivita
          </h3>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-1">
            Posledných {days} dní · Aktívnych teraz: <strong className="text-green-400">{data.summary.active_now}</strong>
          </div>
        </div>
        <div className="inline-flex border border-zinc-800">
          {RANGES.map((r) => (
            <button
              key={r.id}
              onClick={() => setDays(r.id)}
              data-testid={`analytics-range-${r.id}`}
              className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider transition-colors ${
                days === r.id ? "bg-gold-500 text-black" : "text-zinc-400 hover:text-white"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-5">
        <KPI icon={Users} label="Registrácie" value={data.summary.total_registrations} color={COLORS.registrations} />
        <KPI icon={Heart} label="Donations" value={`${data.summary.total_donations} · €${data.summary.total_donation_eur}`} color={COLORS.donations} />
        <KPI icon={Bot} label="AI správ" value={data.summary.total_ai_messages} color={COLORS.ai} />
        <KPI icon={MessageSquare} label="Chat správ" value={data.summary.total_chat_messages} color={COLORS.chat} />
      </div>

      {/* Chart */}
      <div className="h-72 w-full relative">
        {busy && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/40 z-10">
            <Loader2 className="w-5 h-5 animate-spin text-gold-500" />
          </div>
        )}
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={series} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="gReg" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={COLORS.registrations} stopOpacity={0.6} />
                <stop offset="100%" stopColor={COLORS.registrations} stopOpacity={0} />
              </linearGradient>
              <linearGradient id="gDon" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={COLORS.donations} stopOpacity={0.6} />
                <stop offset="100%" stopColor={COLORS.donations} stopOpacity={0} />
              </linearGradient>
              <linearGradient id="gAI" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={COLORS.ai} stopOpacity={0.6} />
                <stop offset="100%" stopColor={COLORS.ai} stopOpacity={0} />
              </linearGradient>
              <linearGradient id="gChat" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={COLORS.chat} stopOpacity={0.6} />
                <stop offset="100%" stopColor={COLORS.chat} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} />
            <XAxis dataKey="dateShort" tick={{ fontSize: 10, fill: "#71717a" }} axisLine={{ stroke: "#27272a" }} tickLine={false} />
            <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#71717a" }} axisLine={{ stroke: "#27272a" }} tickLine={false} />
            <Tooltip content={<CustomTooltip />} cursor={{ stroke: "#D4AF37", strokeOpacity: 0.3 }} />
            <Legend
              wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
              iconType="square"
              formatter={(v) => <span style={{ color: "#a1a1aa" }}>{v}</span>}
            />
            <Area type="monotone" dataKey="registrations" name="Registrácie" stroke={COLORS.registrations} strokeWidth={2} fill="url(#gReg)" />
            <Area type="monotone" dataKey="donations" name="Donations" stroke={COLORS.donations} strokeWidth={2} fill="url(#gDon)" />
            <Area type="monotone" dataKey="ai_messages" name="AI správy" stroke={COLORS.ai} strokeWidth={2} fill="url(#gAI)" />
            <Area type="monotone" dataKey="chat_messages" name="Chat" stroke={COLORS.chat} strokeWidth={2} fill="url(#gChat)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function KPI({ icon: Icon, label, value, color }) {
  return (
    <div className="border-l-2 bg-black/40 p-3" style={{ borderColor: color }}>
      <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500 inline-flex items-center gap-1.5">
        <Icon className="w-3 h-3" style={{ color }} />
        {label}
      </div>
      <div className="font-display font-black text-lg text-white mt-0.5 tabular-nums">{value}</div>
    </div>
  );
}
